import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { 
  DocumentTextIcon, 
  CurrencyDollarIcon,
  CalendarIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { toast } from 'react-toastify';

const ContractInfoSection = ({ project, onUpdate, onBulkDownload }) => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [localFiles, setLocalFiles] = useState([]);
  
  // 초기 파일 목록 설정 및 project 변경 시 업데이트
  React.useEffect(() => {
    const files = project?.ProjectFiles?.filter(file => file.category === 'contract') || [];
    setLocalFiles(files);
  }, [project?.ProjectFiles]);
  
  // 계약 관련 파일 조회
  const contractFiles = localFiles;
  
  // 파일 업로드
  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('category', 'contract');
      
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await api.post(`/projects/${project.id}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('계약서가 업로드되었습니다');
      
      // 로컬 상태 즉시 업데이트
      const contractFilesFromResponse = response.data.files?.filter(file => file.category === 'contract') || [];
      setLocalFiles(contractFilesFromResponse);
      
      // 서버에서 받은 최신 파일 목록으로 캐시 업데이트
      queryClient.setQueryData(['project', project.id], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          ProjectFiles: response.data.files || []
        };
      });
      
      // onUpdate 콜백 실행 (refetch)
      if (onUpdate) {
        await onUpdate();
      }
      
      // Android WebView에서 페이지 새로고침
      if (window.Android) {
        // WebView에서는 React Query 캐시 무효화로 충분하지 않을 수 있음
        // 강제로 페이지 데이터 리로드
        setTimeout(() => {
          queryClient.invalidateQueries(['project', project.id]);
          if (onUpdate) onUpdate();
        }, 100);
      }
      
      setShowUploadModal(false);
      setSelectedFiles([]);
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('파일 업로드에 실패했습니다');
    } finally {
      setUploading(false);
    }
  };
  
  // 파일 삭제
  const handleFileDelete = async (fileId) => {
    if (!window.confirm('이 파일을 삭제하시겠습니까?')) return;
    
    try {
      const response = await api.delete(`/projects/${project.id}/files/${fileId}`);
      toast.success('파일이 삭제되었습니다');
      
      // 로컬 상태 즉시 업데이트
      const contractFilesFromResponse = response.data.files?.filter(file => file.category === 'contract') || [];
      setLocalFiles(contractFilesFromResponse);
      
      // 서버에서 받은 최신 파일 목록으로 캐시 업데이트
      queryClient.setQueryData(['project', project.id], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          ProjectFiles: response.data.files || []
        };
      });
      
      // onUpdate 콜백 실행 (refetch)
      if (onUpdate) {
        await onUpdate();
      }
      
      // Android WebView에서 페이지 새로고침
      if (window.Android) {
        // WebView에서는 React Query 캐시 무효화로 충분하지 않을 수 있음
        // 강제로 페이지 데이터 리로드
        setTimeout(() => {
          queryClient.invalidateQueries(['project', project.id]);
          if (onUpdate) onUpdate();
        }, 100);
      }
    } catch (error) {
      console.error('File delete error:', error);
      toast.error('파일 삭제에 실패했습니다');
    }
  };
  
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW'
    }).format(amount);
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // 파일 경로에서 파일명만 추출
  const getFileName = (file) => {
    // file_name이 있으면 우선 사용
    if (file.file_name) return file.file_name;
    
    // 경로에서 파일명 추출
    const path = file.file_path || '';
    if (!path) return '알 수 없는 파일';
    
    const parts = path.split('/');
    const fullName = parts[parts.length - 1];
    
    // files-timestamp-random.pdf 형식에서 확장자만 유지
    if (fullName.startsWith('files-')) {
      const ext = fullName.split('.').pop();
      const timestamp = fullName.split('-')[1];
      if (timestamp) {
        const date = new Date(parseInt(timestamp));
        if (!isNaN(date.getTime())) {
          return `계약서_${format(date, 'yyyyMMdd_HHmmss')}.${ext}`;
        }
      }
      return `계약서.${ext}`;
    }
    
    return fullName;
  };
  
  // 긴 파일명을 축약 (모바일 대응)
  const getTruncatedFileName = (file) => {
    const fileName = getFileName(file);
    
    // 모바일 여부 확인
    const isMobile = window.innerWidth < 768;
    const maxLength = isMobile ? 25 : 40;
    
    if (fileName.length <= maxLength) return fileName;
    
    // 확장자 분리
    const lastDotIndex = fileName.lastIndexOf('.');
    const extension = lastDotIndex > -1 ? fileName.slice(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex > -1 ? fileName.slice(0, lastDotIndex) : fileName;
    
    // 파일명이 너무 길면 중간 생략
    const availableLength = maxLength - extension.length - 3; // '...' 을 위한 3글자
    if (nameWithoutExt.length > availableLength) {
      // 앞부분을 더 많이 보여주기 (비율 조정)
      const frontLength = Math.ceil(availableLength * 0.6);
      const backLength = Math.floor(availableLength * 0.4);
      return `${nameWithoutExt.slice(0, frontLength)}...${nameWithoutExt.slice(-backLength)}${extension}`;
    }
    
    return fileName;
  };
  
  const canEdit = user?.user_type === 'admin' || user?.user_type === 'contractor';
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">계약 정보</h3>
        {canEdit && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 px-2 py-1 hover:bg-blue-50 rounded transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            파일 추가
          </button>
        )}
      </div>
      
      {/* 계약 금액 */}
      <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-3">
          <CurrencyDollarIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
          <div>
            <p className="text-xs text-gray-500">계약금액</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrency(project.contract_amount)}</p>
          </div>
        </div>
      </div>
      
      {/* 계약 날짜 */}
      {project.contract_date && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <CalendarIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <div>
              <p className="text-xs text-gray-500">계약일</p>
              <p className="text-sm font-medium text-gray-900">
                {format(new Date(project.contract_date), 'yyyy년 MM월 dd일', { locale: ko })}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* 계약서 및 관련 문서 */}
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 mb-2">계약 관련 문서</p>
        
        {contractFiles.length === 0 ? (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
            <DocumentDuplicateIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">아직 업로드된 계약서가 없습니다</p>
            {canEdit && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                계약서 업로드하기
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {contractFiles.map((file) => (
              <div 
                key={file.id}
                className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <DocumentTextIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <a
                          href={(() => {
                            const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
                            // file_path가 이미 uploads/로 시작하면 그대로 사용
                            if (file.file_path.startsWith('uploads/')) {
                              return `${baseUrl}/${file.file_path}`;
                            }
                            // /로 시작하면 그대로 사용
                            if (file.file_path.startsWith('/')) {
                              return `${baseUrl}${file.file_path}`;
                            }
                            // 그 외의 경우 uploads/projects/ 추가
                            return `${baseUrl}/uploads/projects/${file.file_path}`;
                          })()}
                          download={getFileName(file)}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer block truncate"
                          title={getFileName(file)}
                          style={{ wordBreak: 'break-all' }}
                        >
                          {getTruncatedFileName(file)}
                        </a>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatFileSize(file.file_size)} • {format(new Date(file.created_at), 'yyyy.MM.dd')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => {
                            const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
                            let imagePath;
                            // file_path가 이미 uploads/로 시작하면 그대로 사용
                            if (file.file_path.startsWith('uploads/')) {
                              imagePath = `${baseUrl}/${file.file_path}`;
                            }
                            // /로 시작하면 그대로 사용
                            else if (file.file_path.startsWith('/')) {
                              imagePath = `${baseUrl}${file.file_path}`;
                            }
                            // 그 외의 경우 uploads/projects/ 추가
                            else {
                              imagePath = `${baseUrl}/uploads/projects/${file.file_path}`;
                            }
                            window.open(imagePath, '_blank');
                          }}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-white rounded transition-all"
                          title="파일 보기"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <a
                          href={(() => {
                            const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '';
                            // file_path가 이미 uploads/로 시작하면 그대로 사용
                            if (file.file_path.startsWith('uploads/')) {
                              return `${baseUrl}/${file.file_path}`;
                            }
                            // /로 시작하면 그대로 사용
                            if (file.file_path.startsWith('/')) {
                              return `${baseUrl}${file.file_path}`;
                            }
                            // 그 외의 경우 uploads/projects/ 추가
                            return `${baseUrl}/uploads/projects/${file.file_path}`;
                          })()}
                          download={file.file_name || file.file_path}
                          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-white rounded transition-all"
                          title="다운로드"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                        </a>
                        {canEdit && (
                          <button
                            onClick={() => handleFileDelete(file.id)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-all"
                            title="삭제"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 파일 업로드 모달 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">계약서 업로드</h3>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  파일 선택
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.hwp,.jpg,.jpeg,.png"
                  onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  PDF, Word, HWP, 이미지 파일을 업로드할 수 있습니다
                </p>
              </div>
              
              {selectedFiles.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    선택된 파일 ({selectedFiles.length}개)
                  </p>
                  <div className="space-y-1">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="text-sm text-gray-600">
                        • {file.name} ({formatFileSize(file.size)})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFiles([]);
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleFileUpload}
                disabled={selectedFiles.length === 0 || uploading}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractInfoSection;